import { ArrowLeft, Check, Play } from "lucide-react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { CapBadge, CapstoneStar } from "@/components/skills/atoms";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";
import { type ScenarioWord, useScenario, useStartScenario } from "@/lib/scenarios";

// ─── Skills detail ─────────────────────────────────────────────
//
// `scenarios.md` §8.2 + `scenarios-phase4.md` §1.
// Promise hero + 3 inventory blocks (words/rules/constructions) +
// checkpoint preview + sticky CTA.
//
// Phase 4 keeps tap-on-word/rule non-interactive — Phase 5 adds the
// quick-look popups.

export function SkillsDetailPage() {
  const t = useT();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const scenario = useScenario(slug);
  const start = useStartScenario();

  if (!slug) return <Navigate to="/skills" replace />;
  if (scenario.isLoading) return <DetailSkeleton />;
  if (!scenario.data) {
    return (
      <div className="py-10 text-center text-text-3">
        <Link to="/skills" className="underline">
          {t("skills.tag_journal_back")}
        </Link>
      </div>
    );
  }

  const s = scenario.data;
  const status = s.progress?.status ?? "AVAILABLE";
  const isHardLocked = status === "LOCKED";
  const isStarted = status === "IN_PROGRESS" || status === "READY_FOR_CHECKPOINT";
  const isCompleted = status === "COMPLETED" || status === "NEEDS_REVIEW";

  const knownCount = s.words.filter((w) => isWordKnown(w)).length;
  const openCount = s.rules.filter(() => isRuleOpen()).length;
  const wordsRemaining = Math.max(s.words.length - knownCount, 0);

  function handleStart() {
    if (!s) return;
    // If the scenario is already in flight, skip the redundant start
    // mutation and go straight to the session — the controller will
    // resume at the first incomplete step. The start endpoint is
    // idempotent, but skipping the round-trip keeps the UX snappy.
    if (isStarted) {
      navigate(`/skills/${s.slug}/session`);
      return;
    }
    start.mutate(
      { slug: s.slug },
      {
        // After state transition lands, route into the session flow.
        onSuccess: () => navigate(`/skills/${s.slug}/session`),
      },
    );
  }

  return (
    // pb-24 (~96px) leaves room for the sticky CTA (~64px). The
    // bottom nav is hidden on this route (see `hideTabBar` in
    // `App.tsx`), so we don't need its 72px reservation any more.
    <div className="flex flex-col gap-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Link
          to="/skills"
          aria-label="back"
          className="-ml-1 rounded-md p-2 text-text-3 hover:bg-[color:var(--surface-2)]"
        >
          <ArrowLeft className="size-5" />
        </Link>
      </div>

      {/* Promise hero */}
      <div>
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <CapBadge sm>
            {s.level} · {t("skills.tag_scenario")}
          </CapBadge>
          {s.capstone && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-[7px] py-[2px] font-mono text-[10px] font-bold uppercase tracking-wider"
              style={{ background: "var(--scen-bg2)", color: "var(--scen-fg)" }}
            >
              <CapstoneStar size={9} />
              {t("skills.detail_capstone")}
            </span>
          )}
          <span className="font-mono text-[11px] text-text-3">
            {t("skills.detail_progress_minutes", { n: s.estimatedMinutes.toString() })}
          </span>
        </div>
        <h1 className="mb-3 text-[26px] font-bold tracking-tight leading-tight">{s.title}</h1>
        <div
          className="rounded-[var(--r-md)] px-4 py-3.5 border-l-[3px]"
          style={{ background: "var(--scen-bg)", borderLeftColor: "var(--scen-solid)" }}
        >
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--scen-fg)" }}>
            {t("skills.detail_promise_label")}
          </div>
          <p className="text-[14px] leading-relaxed" style={{ color: "var(--scen-ink)" }}>
            {s.promise}
          </p>
        </div>
      </div>

      {/* Words block */}
      <section className="rounded-[var(--r-md)] border border-border bg-surface p-3.5">
        <div className="mb-2.5 flex items-baseline justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-text">{t("skills.detail_words")}</div>
            <div className="font-mono text-[11px] text-text-3">
              {t("skills.detail_words_known", {
                known: knownCount.toString(),
                total: s.words.length.toString(),
              })}
            </div>
          </div>
          {wordsRemaining > 0 ? (
            <CapBadge sm>{t("skills.detail_words_need", { n: wordsRemaining.toString() })}</CapBadge>
          ) : (
            <span className="text-[11px] font-semibold" style={{ color: "var(--scen-fg)" }}>
              {t("skills.detail_words_ready")}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {s.words.map((w) => {
            const known = isWordKnown(w);
            return (
              <span
                key={w.lemma}
                className={cn(
                  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1 text-[12px]",
                )}
                style={{
                  background: known ? "var(--surface-2)" : "var(--scen-bg)",
                  color: known ? "var(--text-2)" : "var(--scen-ink)",
                  borderColor: known ? "var(--border)" : "var(--scen-bg2)",
                }}
              >
                {known && <Check className="size-2.5" style={{ color: "var(--good)" }} strokeWidth={3} />}
                <span className="font-medium">{w.lemma}</span>
                {w.translation && (
                  <span className="text-[11px]" style={{ color: known ? "var(--text-3)" : "var(--scen-fg)" }}>
                    · {w.translation}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </section>

      {/* Rules block */}
      <section className="rounded-[var(--r-md)] border border-border bg-surface p-3.5">
        <div className="mb-2.5">
          <div className="text-[13px] font-semibold text-text">{t("skills.detail_rules")}</div>
          <div className="font-mono text-[11px] text-text-3">
            {t("skills.detail_rules_open", {
              open: openCount.toString(),
              total: s.rules.length.toString(),
            })}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          {s.rules.map((r) => (
            <div
              key={r.slug}
              className="flex items-center gap-2.5 rounded-[var(--r-sm)] border border-border bg-[color:var(--surface-2)] px-3 py-2.5"
            >
              <div
                className="flex size-4.5 shrink-0 items-center justify-center rounded-full"
                style={{ background: "var(--surface-3)", color: "var(--text-3)" }}
              >
                <span className="size-1.5 rounded-full bg-current" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-text">{r.title ?? r.slug}</div>
                {r.summary && <div className="truncate font-mono text-[11px] text-text-3">{r.summary}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Constructions block */}
      <section className="rounded-[var(--r-md)] border border-border bg-surface p-3.5">
        <div className="mb-2.5">
          <div className="text-[13px] font-semibold text-text">{t("skills.detail_constructions")}</div>
          <div className="font-mono text-[11px] text-text-3">
            {t("skills.detail_constructions_count", { count: s.constructions.length.toString() })}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {s.constructions.map((c, i) => (
            <div
              key={i}
              className="flex flex-wrap items-baseline justify-between gap-2 rounded-[var(--r-sm)] bg-[color:var(--surface-2)] px-3 py-2"
            >
              <span className="text-[14px] font-medium text-text">{c.ro}</span>
              <span className="text-[12px] text-text-3">{c.ru}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Checkpoint preview */}
      <div
        className="flex items-start gap-2.5 rounded-[var(--r-md)] border border-dashed px-3.5 py-3"
        style={{ borderColor: "var(--scen-solid)" }}
      >
        <div
          className="flex size-8 shrink-0 items-center justify-center rounded-md"
          style={{ background: "var(--scen-bg)", color: "var(--scen-fg)" }}
        >
          <Check className="size-4" strokeWidth={2} />
        </div>
        <div className="text-[12px] leading-snug text-text-2">
          <b className="text-text">{t("skills.tag_checkpoint")}</b>{" "}
          {t("skills.detail_checkpoint_hint", { count: "6", n: wordsRemaining.toString() })}
        </div>
      </div>

      {/* Sticky CTA — bottom of viewport on mobile. The bottom nav is
          hidden on this route (see `hideTabBar` in `App.tsx`), so the
          CTA anchors at `bottom-0` directly, with safe-area padding
          for iOS home-indicator clearance. */}
      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface px-5 py-3"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <div className="container max-w-[640px]">
          {isHardLocked ? (
            <button
              type="button"
              disabled
              className="flex w-full items-center justify-center gap-2 rounded-[var(--r-md)] bg-[color:var(--surface-2)] px-4 py-3 text-[14px] font-semibold text-text-3"
            >
              {t("skills.section_locked")}
            </button>
          ) : isCompleted ? (
            <Link
              to="/skills"
              className="flex w-full items-center justify-center gap-2 rounded-[var(--r-md)] bg-[color:var(--surface-2)] px-4 py-3 text-[14px] font-semibold text-text-2"
            >
              {t("skills.tag_journal_back")}
            </Link>
          ) : (
            <Button type="button" variant="scenario" size="block" disabled={start.isPending} onClick={handleStart}>
              <Play strokeWidth={2} />
              {isStarted ? t("skills.continue") : t("skills.start_scenario")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tiny derivations ─────────────────────────────────────────
//
// Phase 4 doesn't have per-word user state — the API returns word
// translations but no SRS readout per-lemma. Until Phase 5 wires
// that, we surface "known" as false universally. The visual state
// works regardless: green checkmarks just won't appear yet.
//
// When Phase 5 lands, replace this with a real predicate (likely
// fed from the journal-bulk endpoint extending its DTO with
// per-lemma known booleans).
function isWordKnown(_w: ScenarioWord): boolean {
  return false;
}

function isRuleOpen(): boolean {
  return false;
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="h-8 w-32 animate-pulse rounded-md bg-[color:var(--surface-2)]" />
      <div className="h-12 w-3/4 animate-pulse rounded-md bg-[color:var(--surface-2)]" />
      <div className="h-24 animate-pulse rounded-[var(--r-md)] bg-[color:var(--surface-2)]" />
      <div className="h-32 animate-pulse rounded-[var(--r-md)] bg-[color:var(--surface-2)]" />
      <div className="h-32 animate-pulse rounded-[var(--r-md)] bg-[color:var(--surface-2)]" />
    </div>
  );
}
